import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  listInstanceOutputsHistory,
  type StateCommitDto,
} from '@/services/aiStudioMonitorService';

interface InstanceOutputsHistoryPanelProps {
  instanceId: string;
}

export const InstanceOutputsHistoryPanel: React.FC<InstanceOutputsHistoryPanelProps> = ({
  instanceId,
}) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState<StateCommitDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listInstanceOutputsHistory(instanceId)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  if (loading) {
    return <p className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.history.loading')}</p>;
  }

  if (rows.length === 0) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.history.outputsEmpty')}
      </p>
    );
  }

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.history.outputsTitle')}
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="rounded border border-[var(--color-border)] px-3 py-2 text-base text-[var(--color-surface-text)]"
          >
            <span className="font-medium">v{row.version}</span>
            {' · '}
            {new Date(row.committedAt).toLocaleString()}
            {' · '}
            {String(row.changeSummary['label'] ?? 'Committed plan')}
            {row.changeSummary['assignment_count'] != null && (
              <> — {String(row.changeSummary['assignment_count'])} assignments</>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
